/**
 * minigolfscoreboard.com -- the public site.
 *
 * Until now the root of the domain rendered "Scorecard not found", which is
 * what a golfer sees when a QR code is wrong and what a *prospective customer*
 * saw when they typed the domain from a business card.
 *
 * The pitch is the product's one real differentiator, and it is stated as the
 * thing it is rather than as a feature list: the incumbent shows the top few
 * groups, so most paying customers never see themselves on the screen they are
 * paying for. Everything else here is support for that sentence.
 *
 * Prices are the owner's own: 49.99 a month for a single course, 99.99 for
 * a multi-course venue. They live here rather than in the database because nothing reads
 * them but this page -- the subscription model on `venues` records which plan
 * a venue is on, not what it costs, so a price change is a copy edit and never
 * a migration.
 */

import { useEffect, useState } from "react";
import { Privacy, Terms } from "./Legal";
import { Footer, Nav } from "./Shell";
import { Signup, SignupDone } from "./Signup";
import "./site.css";

/**
 * The pitch, shown rather than described.
 *
 * A leaderboard that shows five of thirteen groups, next to one that shows all
 * of them. This is the entire reason the product exists, and a paragraph about
 * it lands nowhere near as hard as two lists side by side.
 */
function Comparison() {
  const groups = [
    { pos: 1, name: "Bea", raw: 23, rel: "-2" },
    { pos: 2, name: "Marcus", raw: 32, rel: "+1" },
    { pos: 3, name: "The Putt Pirates", raw: 45, rel: "+7" },
    { pos: 4, name: "Nadia", raw: 15, rel: "-1" },
    { pos: 5, name: "Wendell", raw: 21, rel: "-1" },
  ];
  const you = { pos: 9, name: "Your family", raw: 38, rel: "+9" };

  return (
    <div className="site-compare">
      <div className="site-compare__card site-compare__card--bad">
        <p className="site-compare__label">Typical scoreboard: top 5</p>
        {groups.map((g) => (
          <div className="site-row" key={g.pos}>
            <span className="site-row__pos">{g.pos}</span>
            <span className="site-row__name">{g.name}</span>
            <span className="site-row__score">{g.raw}</span>
            <span className="site-row__pill">{g.rel}</span>
          </div>
        ))}
        <div className="site-missing">
          8 more groups playing, and not on the screen
        </div>
      </div>

      <div className="site-compare__card site-compare__card--good">
        <p className="site-compare__label">Mini Golf Scoreboard: everyone</p>
        {groups.slice(0, 4).map((g) => (
          <div className="site-row" key={g.pos}>
            <span className="site-row__pos">{g.pos}</span>
            <span className="site-row__name">{g.name}</span>
            <span className="site-row__score">{g.raw}</span>
            <span className="site-row__pill">{g.rel}</span>
          </div>
        ))}
        <div className="site-row site-row--you">
          <span className="site-row__pos">{you.pos}</span>
          <span className="site-row__name">{you.name}</span>
          <span className="site-row__score">{you.raw}</span>
          <span className="site-row__pill">{you.rel}</span>
        </div>
        <p className="site-note" style={{ marginTop: 10 }}>
          The board scrolls and pages so every group on the course is seen, every
          cycle, last place included.
        </p>
      </div>
    </div>
  );
}

export function Landing() {
  return (
    <div className="site">
      <div className="site__wrap">
        <Nav page="home" />

        <header className="site-hero">
          <div>
            <p className="site-eyebrow">Live scoring for mini golf</p>
            <h1 className="site-h1">Every group on the screen. Not just the winners.</h1>
            <p className="site-lede">
              Golfers scan a code at the first tee and tap in their strokes. Their
              scores appear on your TV within a second, under your name, your
              colours, and with nobody hidden because they are not winning.
            </p>
            <div className="site-cta-row">
              <a className="site-btn site-btn--primary" href="/pricing">
                See pricing
              </a>
              <a className="site-btn site-btn--ghost" href="/venue">
                Venue sign in
              </a>
            </div>
            <p className="site-note">
              No app to download. No accounts for golfers. Works on the wifi you
              already have.
            </p>
          </div>

          <Comparison />
        </header>

        <section className="site-section">
          <p className="site-eyebrow">Why it matters</p>
          <h2 className="site-h2">A family that never sees itself has paid for nothing</h2>
          <p className="site-p">
            The usual scoreboard ranks every group and shows the top few. On a busy
            Saturday that means most of the people who paid you never appear on the
            screen they walked past on the way in. The ones who do are the ones who
            were already winning.
          </p>
          <p className="site-p">
            This board never rank-gates active play. It scrolls at a readable speed,
            and when a course is too busy to scroll through it pages in blocks
            instead, so the cycle takes longer rather than leaving anyone off. A
            family in last place finds themselves within one pass, every time.
          </p>
        </section>

        <section className="site-section">
          <p className="site-eyebrow">How it works</p>
          <h2 className="site-h2">Three things to set up, once</h2>
          <div className="site-steps">
            <div className="site-step">
              <h3>Print the tee codes</h3>
              <p>
                One QR code per course, generated from your admin and mounted at the
                first tee. That is the whole installation for the golfer's side.
              </p>
            </div>
            <div className="site-step">
              <h3>Point a screen at your board</h3>
              <p>
                Any TV with a browser or a cheap stick. Open your board's address
                once and leave it. It runs unattended for weeks and reconnects on
                its own.
              </p>
            </div>
            <div className="site-step">
              <h3>Add your brand</h3>
              <p>
                Your logo, your colours, your course names and pars. Golfers see your
                venue, not ours.
              </p>
            </div>
          </div>
        </section>

        <section className="site-section">
          <p className="site-eyebrow">What you get</p>
          <h2 className="site-h2">Built for a Saturday, not a demo</h2>
          <div className="site-grid">
            <div className="site-card">
              <div className="site-card__icon">◉</div>
              <h3>Scores in under a second</h3>
              <p>
                A stroke tapped at hole seven is on the wall before the group has
                walked to hole eight.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card__icon">⚑</div>
              <h3>Works without signal</h3>
              <p>
                Scoring writes to the phone first. In a dead corner of the course the
                round carries on; the board catches up when the signal does.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card__icon">◧</div>
              <h3>Your brand, not ours</h3>
              <p>
                Logo, colours and tagline across the board and every golfer's phone.
                We stay out of the way.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card__icon">◇</div>
              <h3>Sell the screen</h3>
              <p>
                Local sponsors between rotations and beside the leaderboard, with
                your own master off switch if you would rather not.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card__icon">▤</div>
              <h3>Know your busiest hour</h3>
              <p>
                Rounds per day, average round length, holes in one, and how your
                courses compare.
              </p>
            </div>
            <div className="site-card">
              <div className="site-card__icon">✎</div>
              <h3>Fix it from the counter</h3>
              <p>
                Close a round somebody walked away from, correct a score that went in
                wrong, or clear a group, from a phone or a tablet.
              </p>
            </div>
          </div>
        </section>

        <section className="site-section">
          <p className="site-eyebrow">Privacy</p>
          <h2 className="site-h2">First names only, deleted after 30 days</h2>
          <p className="site-p">
            Children play mini golf, so the data rules are strict by design rather
            than by policy. Golfers give a first name or a nickname and nothing else:
            no accounts, no email, no phone numbers, no ages. Names are deleted 30
            days after the round; the scores stay, so your reports keep working.
          </p>
          <p className="site-p">
            Nothing on the golfer's phone calls a third party. No ad network, no
            analytics, no tracking. Names that would be unwelcome on a screen in a
            room full of children are refused at the tee.
          </p>
        </section>

        <section className="site-section site-section--tight">
          <h2 className="site-h2">See it running</h2>
          <p className="site-p">
            The quickest way to judge a scoreboard is to watch one for a minute on a
            busy afternoon. Get in touch and we will set your venue up with a trial
            board and your own branding.
          </p>
          <div className="site-cta-row" style={{ marginTop: 20 }}>
            <a className="site-btn site-btn--primary" href="/pricing">
              See pricing
            </a>
            <a className="site-btn site-btn--ghost" href="/venue">
              Venue sign in
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}

export function Pricing() {
  return (
    <div className="site">
      <div className="site__wrap">
        <Nav page="pricing" />

        <section className="site-section" style={{ paddingTop: 56 }}>
          <p className="site-eyebrow">Pricing</p>
          <h1 className="site-h2">
            Professional digital scoring, branded entirely for your venue.
          </h1>
          <p className="site-lede">
            Mini Golf Scoreboard gives your venue a fully branded scoring and
            leaderboard platform built around your course. Guests play under
            your brand while you control the course, screens, scoring,
            sponsorships, and venue experience.
          </p>

          <div className="site-plans">
            <div className="site-plan">
              <h3>Starter</h3>
              <p className="site-plan__who">One course, one screen.</p>
              <div className="site-plan__price">
                <span className="site-plan__amount">$49.99</span>
                <span className="site-plan__per">/ month</span>
              </div>
              <ul>
                <li>
                  <strong>Unlimited golfers</strong> and unlimited rounds
                </li>
                <li>One course, one TV board</li>
                <li>Your logo, colours and tagline</li>
                <li>Offline-tolerant scoring</li>
                <li>Reports and course stats</li>
                <li>Live ops from the counter</li>
              </ul>
              <a
                className="site-btn site-btn--ghost"
                href="/signup?plan=starter"
                style={{ width: "100%" }}
              >
                Get started
              </a>
            </div>

            <div className="site-plan site-plan--featured">
              <span className="site-plan__tag">Most venues</span>
              <h3>Pro</h3>
              <p className="site-plan__who">Several courses, several screens.</p>
              <div className="site-plan__price">
                <span className="site-plan__amount">$99.99</span>
                <span className="site-plan__per">/ month</span>
              </div>
              <ul>
                <li>
                  Everything in Starter, plus <strong>unlimited courses</strong>
                </li>
                <li>Unlimited TV boards</li>
                <li>
                  <strong>Sponsor slots</strong>: sell your own screen time
                </li>
                <li>Board pacing you control</li>
                <li>Priority support</li>
              </ul>
              <a
                className="site-btn site-btn--primary"
                href="/signup?plan=pro"
                style={{ width: "100%" }}
              >
                Get started
              </a>
            </div>
          </div>

          <p className="site-note" style={{ marginTop: 22 }}>
            Every plan includes the rule that matters: every group on the course is
            visible on the board, whatever their score.
          </p>
        </section>

        <section className="site-section">
          <h2 className="site-h2">Questions venues actually ask</h2>
          <div className="site-faq">
            <details>
              <summary>Do golfers need to download anything?</summary>
              <p>
                No. They scan the code at the first tee and the scorecard opens in
                their browser. There is no app, no sign-up, and no account. They
                type a first name and start playing.
              </p>
            </details>
            <details>
              <summary>What happens when the wifi drops?</summary>
              <p>
                Scoring keeps working. Strokes are saved on the phone first and sync
                when the signal comes back, so a dead corner of the course means the
                board is briefly out of date, never that a round is lost. Closing the
                browser mid-round does not lose it either.
              </p>
            </details>
            <details>
              <summary>What do I need to put it on a TV?</summary>
              <p>
                Any screen with a browser. A cheap streaming stick is plenty. Open
                your board's address once and leave it. It is built to run
                unattended for weeks and reconnects by itself if the network blips.
              </p>
            </details>
            <details>
              <summary>Can I sell advertising on the board?</summary>
              <p>
                Yes, and it is yours to sell. Local sponsors appear between rotations
                and beside the leaderboard, each with its own display time. There is
                a master off switch if you would rather have no advertising at all.
                That is your call, not a paid upgrade.
              </p>
            </details>
            <details>
              <summary>What do you do with children's names?</summary>
              <p>
                As little as possible. First names or nicknames only, deleted 30 days
                after the round finishes. No emails, no phone numbers, no ages, no
                accounts. Scores are kept so your reports still work, and no report
                can name a golfer after the names are gone.
              </p>
            </details>
            <details>
              <summary>What if we cancel?</summary>
              <p>
                Your board keeps running for a fortnight rather than going dark in
                front of paying customers, then shows a quiet notice. Nothing is
                deleted. If you come back, your rounds and your settings are still
                there.
              </p>
            </details>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}

/** Routes the two public pages. Hand-rolled, like the rest of the app. */
export function PublicSite() {
  const [path, setPath] = useState(() => window.location.pathname);

  // Plain links, so the browser handles navigation. This only keeps the render
  // honest if something else changes the history (a back button).
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const clean = path.replace(/\/$/, "");
  const pricing = clean === "/pricing";
  const signup = clean === "/signup";
  const done = clean === "/signup/done";
  const privacy = clean === "/privacy";
  const terms = clean === "/terms";

  /**
   * No head manipulation here any more.
   *
   * These pages are prerendered (scripts/prerender.mjs), so each one is served
   * with its own title, description, canonical and robots value already in the
   * HTML. Setting them again on mount replaced correct static tags with worse
   * runtime ones -- and the old version flipped `noindex` to `index` with
   * script, which never worked anyway: a crawler acts on the served value
   * before any script runs.
   */


  if (privacy) return <Privacy />;
  if (terms) return <Terms />;
  if (done) return <SignupDone />;
  if (signup) return <Signup />;
  return pricing ? <Pricing /> : <Landing />;
}
